import { OktaAuth, Tokens, AccessToken, IDToken } from '@okta/okta-auth-js';

import {
  OKTA_ACCESS_TOKEN_KEY,
  OKTA_ID_TOKEN_KEY,
} from '@@/common/constants/okta';
import type {
  TSaveableAccessToken,
  TSaveableIdToken,
  TSaveAccessToken,
  TSaveIdToken,
} from '@@/common/interfaces/okta';

import { getOktaIssuer } from '../utils/getOktaIssuer';

type TOktaTokenManagerOptions = {
  oktaAuth: OktaAuth;
  saveAccessTokenInAppState: TSaveAccessToken;
  saveIdTokenInAppState: TSaveIdToken;
};

const oktaClientId = process.env.REACT_APP_OKTA_CLIENT_ID;
const oktaScopes = (process.env.REACT_APP_OKTA_SCOPE ?? '').split(' ');
const oktaIssuer = getOktaIssuer();

/**
 * Manages the fetching and saving of Okta tokens.
 */
export class OktaTokenManager {
  constructor(private opts: TOktaTokenManagerOptions) {}

  get oktaAuth() {
    return this.opts.oktaAuth;
  }

  static isJWT(
    token: TSaveableAccessToken | TSaveableIdToken
  ): token is string {
    return typeof token === 'string';
  }

  /**
   * Given an id token JWT or an already decoded id token JWT payload, return a
   * decoded JWT payload.
   * @param idToken The token to decode.
   */
  private decodeIdTokenJWT(idToken: TSaveableIdToken): IDToken {
    if (!OktaTokenManager.isJWT(idToken)) {
      return idToken;
    }
    const idClaims = this.oktaAuth.token.decode(idToken).payload;
    return {
      claims: idClaims,
      clientId: oktaClientId,
      expiresAt: idClaims.exp || 0,
      idToken,
      scopes: oktaScopes,
      issuer: oktaIssuer,
      authorizeUrl: `${oktaIssuer}/v1/authorize`,
    };
  }

  /**
   * Given an access token JWT or an already decoded access token JWT payload,
   * return a decoded JWT payload.
   * @param accessToken The token to decode.
   */
  private decodeAccessTokenJWT(accessToken: AccessToken): AccessToken {
    if (!OktaTokenManager.isJWT(accessToken)) {
      return accessToken;
    }
    const authClaims = this.oktaAuth.token.decode(accessToken).payload;
    return {
      accessToken,
      expiresAt: authClaims.exp ?? 0,
      scopes: oktaScopes,
      tokenType: 'Bearer',
      authorizeUrl: `${oktaIssuer}/v1/authorize`,
      claims: authClaims,
      userinfoUrl: `${oktaIssuer}/v1/userinfo`,
    };
  }

  /**
   * Persist a decoded access token JWT payload for future use.
   * @param accessToken The token to store.
   */
  private saveAccessToken(accessToken: AccessToken) {
    const decodedJWT = this.decodeAccessTokenJWT(accessToken);

    this.oktaAuth.tokenManager.add(OKTA_ACCESS_TOKEN_KEY, decodedJWT);
    this.opts.saveAccessTokenInAppState(decodedJWT);
  }

  /**
   * Persist a decoded id token JWT payload for future use.
   * @param idToken The token to store.
   */
  private saveIdToken(idToken: IDToken) {
    const decodedJWT = this.decodeIdTokenJWT(idToken);

    this.oktaAuth.tokenManager.add(OKTA_ID_TOKEN_KEY, decodedJWT);
    this.opts.saveIdTokenInAppState(decodedJWT);
  }

  /**
   * Ensure tokens are stored within both Okta's token manager and the
   * app's internal store.
   * @param tokens The access token and id token to be saved.
   */
  public async save(tokens: Tokens) {
    await Promise.all([
      tokens.accessToken && this.saveAccessToken(tokens.accessToken),
      tokens.idToken && this.saveIdToken(tokens.idToken),
    ]);
  }

  /**
   * If an active session exists, fetch and save access and id tokens.
   * @param opts.shouldLoadTokensFromSession Determines whether or not tokens are fetched and saved.
   * @param opts.onSessionFound Callback to execute if a session is found.
   */
  public async load({
    shouldLoadTokensFromSession = true,
    onSessionFound = () => {},
  }: {
    shouldLoadTokensFromSession?: boolean;
    onSessionFound?: () => void;
  } = {}) {
    const { status } = await this.oktaAuth.session.get();
    if (status === 'ACTIVE') {
      onSessionFound();
    } else {
      throw new Error('loadOktaTokens - No active session');
    }

    if (shouldLoadTokensFromSession) {
      const { tokens } = await this.oktaAuth.token.getWithoutPrompt({
        responseType: ['id_token', 'token'],
      });
      this.save(tokens);
    }
  }
}
